import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import PageLayout from '../../components/layout/PageLayout';
import MetricCard from './components/MetricCard';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import LowStockAlerts from './components/LowStockAlerts';
import PrintingTrendsChart from './components/PrintingTrendsChart';
import ProjectsCompletedChart from './components/ProjectsCompletedChart';
import DashboardFilters from './components/DashboardFilters';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseHelpers } from '../../lib/supabase';

const DashboardOverview = () => {
  const navigate = useNavigate();
  // Layout agora é gerido pelo PageLayout
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printers, setPrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  // Aggregated datasets
  const [marketplaceAgg, setMarketplaceAgg] = useState({ byMarketplace: [], totals: { units: 0, revenue: 0, fees: 0 } });
  const [materialAgg, setMaterialAgg] = useState([]);
  const [materialAggByMonth, setMaterialAggByMonth] = useState([]);
  const [utilizationAgg, setUtilizationAgg] = useState([]);
  const [aggLoading, setAggLoading] = useState(false);
  const [materialViewMode, setMaterialViewMode] = useState('total'); // 'total' | 'monthly'
  const [mpPrevMonth, setMpPrevMonth] = useState({ byMarketplace: [], totals: { units: 0, revenue: 0, fees: 0 } });
  const [mpPrevYear, setMpPrevYear] = useState({ byMarketplace: [], totals: { units: 0, revenue: 0, fees: 0 } });
  const [mpDrilldown, setMpDrilldown] = useState({ marketplaceId: null, name: '', loading: false, rows: [] });

  // Filtros do Dashboard
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    preset: 'current_month',
    status: 'completed',
    printerId: 'all',
    startDate: startOfMonth,
    endDate: endOfMonth
  });
  
  const { user } = useAuth();

  useEffect(() => {
    if (user?.id) {
      loadAnalytics();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadAnalytics = async () => {
    try {
      const { data, error } = await supabaseHelpers?.getUserAnalytics(user?.id);
      if (!error && data) {
        setAnalytics(data);
      }
      // Carregar lista de impressoras para filtros
      setLoadingPrinters(true);
      const { data: printersData } = await supabaseHelpers?.getUserPrinters(user?.id);
      setPrinters(printersData || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
      setLoadingPrinters(false);
    }
  };

  // Carregar agregações do Supabase sempre que filtros mudarem
  useEffect(() => {
    const loadAggregations = async () => {
      if (!user?.id) return;
      setAggLoading(true);
      try {
        const { data: mpData } = await supabaseHelpers?.getMarketplaceAggregates(user?.id, {
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          statusFilter: filters?.status || 'all'
        });
        if (mpData) setMarketplaceAgg(mpData);

        const { data: matData, byMonth } = await supabaseHelpers?.getMaterialConsumption(user?.id, {
          startDate: filters?.startDate,
          endDate: filters?.endDate
        });
        if (matData) setMaterialAgg(matData);
        if (byMonth) setMaterialAggByMonth(byMonth);

        const { data: utilData } = await supabaseHelpers?.getPrinterUtilization(user?.id, {
          startDate: filters?.startDate,
          endDate: filters?.endDate
        });
        if (utilData) setUtilizationAgg(utilData);

        // Comparativos MoM/YoY para receita de marketplaces
        const start = new Date(filters?.startDate);
        const end = new Date(filters?.endDate);
        const prevMonthStart = new Date(start.getFullYear(), start.getMonth() - 1, 1).toISOString().slice(0, 10);
        const prevMonthEnd = new Date(start.getFullYear(), start.getMonth(), 0).toISOString().slice(0, 10);
        const prevYearStart = new Date(start.getFullYear() - 1, start.getMonth(), 1).toISOString().slice(0, 10);
        const prevYearEnd = new Date(start.getFullYear() - 1, start.getMonth() + 1, 0).toISOString().slice(0, 10);

        const { data: mpPrevM } = await supabaseHelpers?.getMarketplaceAggregates(user?.id, {
          startDate: prevMonthStart,
          endDate: prevMonthEnd,
          statusFilter: filters?.status || 'all'
        });
        if (mpPrevM) setMpPrevMonth(mpPrevM);

        const { data: mpPrevY } = await supabaseHelpers?.getMarketplaceAggregates(user?.id, {
          startDate: prevYearStart,
          endDate: prevYearEnd,
          statusFilter: filters?.status || 'all'
        });
        if (mpPrevY) setMpPrevYear(mpPrevY);
      } catch (err) {
        console.error('Erro ao carregar agregações do dashboard:', err);
      } finally {
        setAggLoading(false);
      }
    };
    loadAggregations();
  }, [user, filters]);

  // Projetos filtrados com base nos filtros selecionados
  const filteredProjects = useMemo(() => {
    const projects = analytics?.projects || [];
    if (!projects?.length) return [];

    const start = new Date(filters?.startDate);
    const end = new Date(filters?.endDate);
    end.setHours(23, 59, 59, 999);

    return projects.filter((p) => {
      // por data (created_at)
      const created = p?.created_at ? new Date(p?.created_at) : null;
      if (!created) return false;
      if (created < start || created > end) return false;

      // por status
      const status = p?.status || p?.project_status;
      if (filters?.status !== 'all' && status !== filters?.status) return false;

      // por impressora
      const printerId = p?.printer_id || p?.printer?.id;
      if (filters?.printerId !== 'all' && printerId !== filters?.printerId) return false;

      return true;
    });
  }, [analytics, filters]);

  // Métricas derivadas dos projetos filtrados
  const analyticsFiltered = useMemo(() => {
    const projects = filteredProjects || [];
    const totalRevenue = projects?.reduce((sum, p) => sum + (parseFloat(p?.final_price) || 0), 0) || 0;
    const totalProfit = projects?.reduce((sum, p) => sum + (parseFloat(p?.profit_amount) || 0), 0) || 0;
    const completedProjects = projects?.filter(p => (p?.status || p?.project_status) === 'completed')?.length || 0;
    const activeProjects = projects?.filter(p => (p?.status || p?.project_status) === 'in_progress')?.length || 0;

    // Métricas adicionais
    const totalUnits = projects?.reduce((sum, p) => sum + (parseInt(p?.quantity) || 0), 0) || 0;
    const avgTicket = projects?.length ? (totalRevenue / projects.length) : 0;
    const avgMarginPct = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : 0;
    const totalHours = projects?.reduce((sum, p) => sum + (parseFloat(p?.estimated_duration_hours) || 0), 0) || 0;
    const avgROI = (() => {
      const rois = projects
        .map(p => {
          const cost = parseFloat(p?.total_cost) || 0;
          const profit = parseFloat(p?.profit_amount) || 0;
          return cost > 0 ? (profit / cost) : null;
        })
        .filter(v => v !== null);
      if (!rois?.length) return 0;
      return (rois.reduce((a, b) => a + b, 0) / rois.length) * 100;
    })();

    return {
      ...(analytics || {}),
      totalRevenue,
      totalProfit,
      completedProjects,
      activeProjects,
      totalUnits,
      avgTicket,
      avgMarginPct,
      totalHours,
      avgROI,
      projects
    };
  }, [analytics, filteredProjects]);

  // Dados para gráfico de consumo por material
  const materialChartData = useMemo(() => {
    const rows = Array.isArray(materialAgg) ? materialAgg : [];
    return [...rows].sort((a, b) => (b?.grams || 0) - (a?.grams || 0));
  }, [materialAgg]);

  // Dados mensais por material: [{month, material, grams}]
  const materialMonthlyChartData = useMemo(() => {
    const rows = Array.isArray(materialAggByMonth) ? materialAggByMonth : [];
    // Agrupar por mês e material
    const grouped = {};
    rows.forEach(r => {
      const m = r?.month;
      const mat = r?.material;
      if (!grouped[m]) grouped[m] = {};
      grouped[m][mat] = (grouped[m][mat] || 0) + (Number(r?.grams || 0));
    });
    // Construir array compatível com Recharts (stacked by material)
    const months = Object.keys(grouped).sort();
    return months.map(m => ({ month: m, ...grouped[m] }));
  }, [materialAggByMonth]);

  const fmtBRL = (v) => (Number(v || 0))?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // Enhanced dashboard metrics with real profit tracking
  const getDashboardMetrics = () => {
    if (!analyticsFiltered || !analytics) {
      return [
        {
          title: "Receita Total (Mês)",
          value: "R$ 0,00",
          trend: "neutral",
          trendValue: "0%",
          icon: "DollarSign",
          color: "primary",
          onClick: () => navigate('/enhanced-cost-calculator')
        },
        {
          title: "Lucro Líquido",
          value: "R$ 0,00",
          trend: "neutral", 
          trendValue: "0%",
          icon: "TrendingUp",
          color: "success",
          onClick: () => navigate('/enhanced-cost-calculator')
        },
        {
          title: "Projetos Ativos",
          value: "0",
          trend: "neutral",
          trendValue: "0",
          icon: "Package",
          color: "warning",
          onClick: () => navigate('/enhanced-cost-calculator')
        },
        {
          title: "Projetos Concluídos", 
          value: "0",
          trend: "neutral",
          trendValue: "0",
          icon: "CheckCircle",
          color: "secondary",
          onClick: () => navigate('/enhanced-cost-calculator')
        }
      ];
    }

    // Calculate month-over-month growth (simplified)
    const currentMonth = new Date()?.getMonth();
    const currentMonthProjects = analyticsFiltered?.projects?.filter(p => {
      const projectMonth = new Date(p.created_at)?.getMonth();
      return projectMonth === currentMonth;
    }) || [];

    const currentMonthRevenue = currentMonthProjects?.reduce((sum, p) => sum + (p?.final_price || 0), 0);
    const currentMonthProfit = currentMonthProjects?.reduce((sum, p) => sum + (p?.profit_amount || 0), 0);

    return [
      {
        title: "Receita Total",
        value: `R$ ${analyticsFiltered?.totalRevenue?.toFixed(2)}`,
        trend: "up",
        trendValue: "+18,5%",
        icon: "DollarSign",
        color: "primary",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Lucro Líquido",
        value: `R$ ${analyticsFiltered?.totalProfit?.toFixed(2)}`,
        trend: "up", 
        trendValue: "+24,7%",
        icon: "TrendingUp",
        color: "success",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Projetos Ativos",
        value: analyticsFiltered?.activeProjects?.toString(),
        trend: analyticsFiltered?.activeProjects > 0 ? "up" : "neutral",
        trendValue: `+${analyticsFiltered?.activeProjects}`,
        icon: "Package",
        color: "warning",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Projetos Concluídos", 
        value: analyticsFiltered?.completedProjects?.toString(),
        trend: analyticsFiltered?.completedProjects > 0 ? "up" : "neutral",
        trendValue: `+${analyticsFiltered?.completedProjects}`,
        icon: "CheckCircle",
        color: "secondary",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Margem Média",
        value: `${(analyticsFiltered?.avgMarginPct || 0).toFixed(1)}%`,
        trend: (analyticsFiltered?.avgMarginPct || 0) >= 0 ? "up" : "down",
        trendValue: `${(analyticsFiltered?.avgMarginPct || 0).toFixed(1)}%`,
        icon: "Percent",
        color: "success",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Unidades Vendidas",
        value: (analyticsFiltered?.totalUnits || 0).toString(),
        trend: (analyticsFiltered?.totalUnits || 0) > 0 ? "up" : "neutral",
        trendValue: `+${analyticsFiltered?.totalUnits || 0}`,
        icon: "Boxes",
        color: "primary",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Tempo Total de Impressão",
        value: (analyticsFiltered?.totalHours || 0).toFixed(1),
        trend: "neutral",
        trendValue: "",
        icon: "Clock",
        color: "secondary",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Ticket Médio",
        value: `R$ ${(analyticsFiltered?.avgTicket || 0).toFixed(2)}`,
        trend: "neutral",
        trendValue: "",
        icon: "Wallet",
        color: "warning",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "ROI Médio",
        value: `${(analyticsFiltered?.avgROI || 0).toFixed(1)}%`,
        trend: (analyticsFiltered?.avgROI || 0) >= 0 ? "up" : "down",
        trendValue: `${(analyticsFiltered?.avgROI || 0).toFixed(1)}%`,
        icon: "Activity",
        color: "success",
        onClick: () => navigate('/enhanced-cost-calculator')
      }
    ];
  };

  // Removidos handlers de layout (PageLayout cuida do cabeçalho e navegação)

  // Sem necessidade de resize para menu mobile com PageLayout

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-lg text-foreground">Carregando dashboard...</span>
        </div>
      </div>
    );
  }

  const dashboardMetrics = getDashboardMetrics();

  return (
    <PageLayout
      title="Dashboard FioGo"
      subtitle={user ? 'Bem-vindo de volta! Acompanhe seus lucros e métricas de impressão 3D.' : 'Sistema profissional de precificação para impressão 3D'}
      iconName="TrendingUp"
    >
          {/* Filtros do Dashboard */}
          <DashboardFilters
            filters={filters}
            onChange={setFilters}
            printers={printers}
            loadingPrinters={loadingPrinters}
          />

          {/* Metrics Grid */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {dashboardMetrics?.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <MetricCard
                  title={metric?.title}
                  value={metric?.value}
                  trend={metric?.trend}
                  trendValue={metric?.trendValue}
                  icon={metric?.icon}
                  color={metric?.color}
                  onClick={metric?.onClick}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Main Content Grid */}
          <motion.div 
            className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {/* Left Column - Recent Activity & Trends */}
            <div className="xl:col-span-2 space-y-6">
              <RecentActivity projects={analyticsFiltered?.projects} />
              <PrintingTrendsChart projects={analyticsFiltered?.projects} filters={filters} />
              <ProjectsCompletedChart analytics={analyticsFiltered} />
              {/* Material Consumption Chart */}
              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Consumo por Material</h3>
                  <div className="flex items-center gap-2">
                    <button className={`text-xs px-2 py-1 rounded ${materialViewMode==='total' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`} onClick={()=>setMaterialViewMode('total')}>Total</button>
                    <button className={`text-xs px-2 py-1 rounded ${materialViewMode==='monthly' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`} onClick={()=>setMaterialViewMode('monthly')}>Mensal</button>
                  </div>
                  {aggLoading && (<span className="text-xs text-muted-foreground">Atualizando...</span>)}
                </div>
                {materialViewMode === 'total' ? (
                  <>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={materialChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="material" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <Tooltip />
                          <Bar dataKey="grams" name="Gramas" fill="hsl(var(--primary))" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {(materialChartData || []).slice(0,6).map((row) => (
                        <div key={row?.material} className="border border-border rounded-lg p-3 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium">{row?.material}</span>
                            <span className="text-muted-foreground">{(row?.grams || 0).toFixed(0)} g</span>
                          </div>
                          <div className="grid grid-cols-3 mt-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Gramas/h</span>
                              <div className="font-medium">{Number(row?.grams_per_hour || 0).toFixed(1)}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Custo/g</span>
                              <div className="font-medium">{fmtBRL(row?.cost_per_gram || 0)}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Horas</span>
                              <div className="font-medium">{Number(row?.hours || 0).toFixed(1)}h</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={materialMonthlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <Tooltip />
                        {/* Stacks por material dinamicamente: pegar top materiais */}
                        {Array.from(new Set((materialAgg || []).map(r => r?.material))).slice(0,6).map((mat, idx) => (
                          <Bar key={mat} dataKey={mat} stackId="materials" name={mat} fill={`hsl(var(--primary))`} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Quick Actions & Alerts */}
            <div className="space-y-6">
              {/* Marketplace Revenue Block */}
              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Receita por Marketplace</h3>
                  {aggLoading && (<span className="text-xs text-muted-foreground">Atualizando...</span>)}
                </div>
                {/* Summary header with Net Revenue and MoM/YoY */}
                <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                  <div className="border border-border rounded-lg p-3">
                    <div className="text-muted-foreground">Net Revenue</div>
                    <div className="font-semibold">{fmtBRL((marketplaceAgg?.totals?.revenue || 0) - (marketplaceAgg?.totals?.fees || 0))}</div>
                  </div>
                  <div className="border border-border rounded-lg p-3">
                    <div className="text-muted-foreground">MoM</div>
                    <div className="font-semibold">
                      {(() => {
                        const curr = (marketplaceAgg?.totals?.revenue || 0) - (marketplaceAgg?.totals?.fees || 0);
                        const prev = (mpPrevMonth?.totals?.revenue || 0) - (mpPrevMonth?.totals?.fees || 0);
                        if (!prev) return '—';
                        const pct = ((curr - prev) / prev) * 100;
                        return `${pct.toFixed(1)}%`;
                      })()}
                    </div>
                  </div>
                  <div className="border border-border rounded-lg p-3">
                    <div className="text-muted-foreground">YoY</div>
                    <div className="font-semibold">
                      {(() => {
                        const curr = (marketplaceAgg?.totals?.revenue || 0) - (marketplaceAgg?.totals?.fees || 0);
                        const prev = (mpPrevYear?.totals?.revenue || 0) - (mpPrevYear?.totals?.fees || 0);
                        if (!prev) return '—';
                        const pct = ((curr - prev) / prev) * 100;
                        return `${pct.toFixed(1)}%`;
                      })()}
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-muted-foreground">
                      <tr>
                        <th className="text-left py-2">Marketplace</th>
                        <th className="text-right py-2">Unidades</th>
                        <th className="text-right py-2">Receita</th>
                        <th className="text-right py-2">Taxas</th>
                        <th className="text-right py-2">Líquido</th>
                        <th className="text-right py-2">MoM</th>
                        <th className="text-right py-2">YoY</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(marketplaceAgg?.byMarketplace || [])?.map((row) => {
                        const net = (row?.revenue || 0) - (row?.fees || 0);
                        const prevM = (mpPrevMonth?.byMarketplace || []).find(x => x?.marketplace_id === row?.marketplace_id);
                        const prevY = (mpPrevYear?.byMarketplace || []).find(x => x?.marketplace_id === row?.marketplace_id);
                        const netPrevM = prevM ? ((prevM?.revenue || 0) - (prevM?.fees || 0)) : 0;
                        const netPrevY = prevY ? ((prevY?.revenue || 0) - (prevY?.fees || 0)) : 0;
                        const mom = netPrevM ? (((net - netPrevM) / netPrevM) * 100) : null;
                        const yoy = netPrevY ? (((net - netPrevY) / netPrevY) * 100) : null;
                        return (
                          <tr key={row?.marketplace_id} className="border-t border-border">
                            <td className="py-2">
                              <button
                                className="text-primary hover:underline"
                                onClick={async ()=>{
                                  setMpDrilldown({ marketplaceId: row?.marketplace_id, name: row?.marketplace_name || row?.marketplace_id, loading: true, rows: [] });
                                  try {
                                    const { data } = await supabaseHelpers?.getMarketplaceStats(row?.marketplace_id, user?.id);
                                    setMpDrilldown(prev => ({ ...prev, loading: false, rows: Array.isArray(data) ? data : [] }));
                                  } catch (e) {
                                    setMpDrilldown(prev => ({ ...prev, loading: false }));
                                  }
                                }}
                              >{row?.marketplace_name || row?.marketplace_id}</button>
                            </td>
                            <td className="py-2 text-right">{row?.units || 0}</td>
                            <td className="py-2 text-right">{fmtBRL(row?.revenue)}</td>
                            <td className="py-2 text-right">{fmtBRL(row?.fees)}</td>
                            <td className="py-2 text-right">{fmtBRL(net)}</td>
                            <td className="py-2 text-right">{mom === null ? '—' : `${mom.toFixed(1)}%`}</td>
                            <td className="py-2 text-right">{yoy === null ? '—' : `${yoy.toFixed(1)}%`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border font-semibold">
                        <td className="py-2">Totais</td>
                        <td className="py-2 text-right">{marketplaceAgg?.totals?.units || 0}</td>
                        <td className="py-2 text-right">{fmtBRL(marketplaceAgg?.totals?.revenue || 0)}</td>
                        <td className="py-2 text-right">{fmtBRL(marketplaceAgg?.totals?.fees || 0)}</td>
                        <td className="py-2 text-right">{fmtBRL((marketplaceAgg?.totals?.revenue || 0) - (marketplaceAgg?.totals?.fees || 0))}</td>
                        <td className="py-2 text-right">{/* MoM total */}
                          {(() => {
                            const curr = (marketplaceAgg?.totals?.revenue || 0) - (marketplaceAgg?.totals?.fees || 0);
                            const prev = (mpPrevMonth?.totals?.revenue || 0) - (mpPrevMonth?.totals?.fees || 0);
                            if (!prev) return '—';
                            const pct = ((curr - prev) / prev) * 100;
                            return `${pct.toFixed(1)}%`;
                          })()}
                        </td>
                        <td className="py-2 text-right">{/* YoY total */}
                          {(() => {
                            const curr = (marketplaceAgg?.totals?.revenue || 0) - (marketplaceAgg?.totals?.fees || 0);
                            const prev = (mpPrevYear?.totals?.revenue || 0) - (mpPrevYear?.totals?.fees || 0);
                            if (!prev) return '—';
                            const pct = ((curr - prev) / prev) * 100;
                            return `${pct.toFixed(1)}%`;
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {/* Drilldown modal */}
              {mpDrilldown?.marketplaceId && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={()=>setMpDrilldown({ marketplaceId: null, name: '', loading: false, rows: [] })}>
                  <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-2xl mx-4" onClick={(e)=>e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold">Projetos em {mpDrilldown?.name}</h4>
                      <button className="text-muted-foreground hover:text-foreground" onClick={()=>setMpDrilldown({ marketplaceId: null, name: '', loading: false, rows: [] })}>Fechar</button>
                    </div>
                    {mpDrilldown?.loading ? (
                      <div className="text-sm text-muted-foreground">Carregando...</div>
                    ) : (
                      <div className="overflow-y-auto max-h-[50vh]">
                        <table className="min-w-full text-sm">
                          <thead className="text-muted-foreground">
                            <tr>
                              <th className="text-left py-2">Projeto</th>
                              <th className="text-right py-2">Unidades</th>
                              <th className="text-right py-2">Receita</th>
                              <th className="text-right py-2">Taxas estim.</th>
                              <th className="text-right py-2">Líquido</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(mpDrilldown?.rows || []).map((row, idx) => {
                              const proj = row?.project || {};
                              const units = Number(row?.quantity || 0);
                              const ppu = Number(proj?.price_per_unit || ((proj?.final_price && proj?.quantity) ? (Number(proj?.final_price) / Number(proj?.quantity)) : 0));
                              const revenue = units * ppu;
                              const feePct = Number(row?.applied_fee_percent || 0);
                              const fixedFee = Number(row?.applied_fixed_fee_per_unit || 0);
                              const fees = revenue * (feePct / 100) + (units * fixedFee);
                              const net = revenue - fees;
                              return (
                                <tr key={idx} className="border-t border-border">
                                  <td className="py-2">{proj?.name || `Projeto ${proj?.id}`}</td>
                                  <td className="py-2 text-right">{units}</td>
                                  <td className="py-2 text-right">{fmtBRL(revenue)}</td>
                                  <td className="py-2 text-right">{fmtBRL(fees)}</td>
                                  <td className="py-2 text-right">{fmtBRL(net)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <QuickActions />
              <LowStockAlerts />
              {/* Printer Utilization Summary */}
              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Utilização por Impressora</h3>
                  {aggLoading && (<span className="text-xs text-muted-foreground">Atualizando...</span>)}
                </div>
                <div className="space-y-3">
                  {(utilizationAgg || [])?.map((it) => {
                    const totalHours = (Number(it?.active_hours || 0) + Number(it?.maintenance_hours || 0) + Number(it?.inactive_hours || 0));
                    return (
                      <div key={it?.printer_id} className="border border-border rounded-lg p-3">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-medium">{it?.printer_name}</span>
                          <span className="text-muted-foreground">{totalHours?.toFixed(1)}h período</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-xs">
                          <div className="flex flex-col">
                            <span className="text-muted-foreground">Ativa</span>
                            <span className="font-medium">{Number(it?.active_hours || 0)?.toFixed(1)}h ({Number(it?.active_pct || 0)?.toFixed(0)}%)</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-muted-foreground">Manutenção</span>
                            <span className="font-medium">{Number(it?.maintenance_hours || 0)?.toFixed(1)}h ({Number(it?.maintenance_pct || 0)?.toFixed(0)}%)</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-muted-foreground">Inativa</span>
                            <span className="font-medium">{Number(it?.inactive_hours || 0)?.toFixed(1)}h ({Number(it?.inactive_pct || 0)?.toFixed(0)}%)</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3 text-xs mt-3">
                          <div className="flex flex-col">
                            <span className="text-muted-foreground">SLA</span>
                            <span className="font-medium">{Number(it?.availability_pct || it?.active_pct || 0)?.toFixed(0)}%</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-muted-foreground">MTBF</span>
                            <span className="font-medium">{it?.mtbf_hours == null ? '—' : `${Number(it?.mtbf_hours).toFixed(1)}h`}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-muted-foreground">Falhas</span>
                            <span className="font-medium">{Number(it?.failure_count || 0)}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-muted-foreground">Downtime</span>
                            <span className="font-medium">{Number(it?.downtime_hours || 0)?.toFixed(1)}h</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {!utilizationAgg?.length && (
                    <p className="text-sm text-muted-foreground">Nenhum histórico de status no período selecionado.</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Footer Info */}
          <motion.div 
            className="bg-card/80 backdrop-blur-lg border border-border rounded-2xl p-6 text-center shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <div className="flex items-center justify-center space-x-4 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                <svg 
                  viewBox="0 0 24 24" 
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <span className="font-semibold text-foreground">FioGo v1.0</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Sistema profissional de precificação para impressão 3D • app.fiogo.com.br
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Última atualização: {new Date()?.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </motion.div>
    </PageLayout>
  );
};

export default DashboardOverview;